"use client";

// shadcn/ui combobox: https://ui.shadcn.com/r/styles/new-york-v4/combobox.json
// Uses existing input/button styles and Tailwind 3 utilities.
import * as React from "react";
import { Combobox as ComboboxPrimitive } from "@base-ui/react/combobox";
import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "~/utils";
import { Button } from "./button";
import { Input } from "./input";

const Combobox = ComboboxPrimitive.Root;
const ComboboxValue = ComboboxPrimitive.Value;

function ComboboxInput({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  ...props
}: ComboboxPrimitive.Input.Props & { showTrigger?: boolean; showClear?: boolean }) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "flex items-center rounded-md border border-gray-200 bg-white pr-2 focus-within:ring-1 focus-within:ring-blue-500",
        className,
      )}
    >
      <ComboboxPrimitive.Input
        render={
          <Input
            disabled={disabled}
            className="min-w-0 flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        }
        {...props}
      />
      {showTrigger ? (
        <ComboboxPrimitive.Trigger
          render={<Button variant="ghost" size="icon" />}
          disabled={disabled}
          aria-label={t("Wählen Sie eine Option")}
        >
          <ChevronDownIcon className="size-4 text-gray-500" />
        </ComboboxPrimitive.Trigger>
      ) : null}
      {showClear ? (
        <ComboboxPrimitive.Clear
          render={<Button variant="ghost" size="icon" />}
          disabled={disabled}
          aria-label={t("Entfernen")}
        >
          <XIcon className="size-4" />
        </ComboboxPrimitive.Clear>
      ) : null}
      {children}
    </div>
  );
}

function ComboboxContent({
  className,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  alignOffset = 0,
  anchor,
  container,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<ComboboxPrimitive.Positioner.Props, "side" | "align" | "sideOffset" | "alignOffset" | "anchor"> &
  Pick<ComboboxPrimitive.Portal.Props, "container">) {
  return (
    <ComboboxPrimitive.Portal container={container}>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-50"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          className={cn(
            "group/combobox-content relative max-h-[var(--available-height)] w-[var(--anchor-width)] max-w-[var(--available-width)] overflow-hidden rounded-md border border-gray-200 bg-white text-gray-950 shadow-md",
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn(
        "max-h-[min(24rem,var(--available-height))] scroll-py-1 overflow-y-auto p-1 data-[empty]:p-0",
        className,
      )}
      {...props}
    />
  );
}

function ComboboxItem({ className, children, ...props }: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        "relative flex w-full cursor-default select-none items-center gap-2 rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-gray-100 data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        render={<span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />}
      >
        <CheckIcon className="size-4" />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  );
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        "hidden w-full justify-center py-2 text-center text-sm text-gray-500 group-data-[empty]/combobox-content:flex",
        className,
      )}
      {...props}
    />
  );
}

const ComboboxChips = React.forwardRef<HTMLDivElement, ComboboxPrimitive.Chips.Props>(function ComboboxChips(
  { className, ...props },
  ref,
) {
  return (
    <ComboboxPrimitive.Chips
      ref={ref}
      data-slot="combobox-chips"
      className={cn(
        "flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm focus-within:ring-1 focus-within:ring-blue-500",
        className,
      )}
      {...props}
    />
  );
});

function ComboboxChip({ className, children, ...props }: ComboboxPrimitive.Chip.Props) {
  const { t } = useTranslation();
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn(
        "flex w-fit items-center justify-center gap-1 rounded-sm bg-gray-100 px-1.5 text-xs font-medium",
        className,
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ChipRemove render={<Button variant="ghost" size="icon" />} aria-label={t("Entfernen")}>
        <XIcon className="size-3" />
      </ComboboxPrimitive.ChipRemove>
    </ComboboxPrimitive.Chip>
  );
}

function ComboboxChipsInput({ className, ...props }: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={cn("min-w-16 flex-1 border-0 bg-transparent outline-none focus:ring-0", className)}
      {...props}
    />
  );
}

function useComboboxAnchor() {
  return React.useRef<HTMLDivElement | null>(null);
}

export {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxValue,
  useComboboxAnchor,
};
